import { X } from "lucide-react";
import prettyBytes from "pretty-bytes";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import { MediaViewer } from "@/components/media-viewer";

import type { PostType } from "../post-type-step/postTypes";
import type { DesignFile } from "./types";

import { PublicationPreview } from "../PublicationPreview";
import { isImageDesign } from "./isImageDesign";

export function DesignPreview({
  brandId,
  designs,
  onClose,
  postType,
}: {
  brandId: number;
  designs: DesignFile[];
  onClose: () => void;
  postType: PostType;
}) {
  const [design] = designs;

  if (!design) return null;

  const isMultiple = designs.length > 1;

  return (
    <aside
      aria-label="Design preview"
      className="flex w-[360px] shrink-0 flex-col overflow-hidden rounded-xl border border-border-secondary bg-bg-primary"
    >
      <header className="flex items-start justify-between gap-md border-b border-border-secondary p-lg">
        <div className="flex min-w-0 flex-col gap-xs">
          {isMultiple ? (
            <>
              <h3 className="truncate text-md font-semibold text-text-primary">
                {designs.length} designs selected
              </h3>
              <p className="text-xs text-text-tertiary">
                They publish in the order you picked them.
              </p>
            </>
          ) : (
            <>
              <h3
                className="truncate text-md font-semibold text-text-primary"
                title={design.name}
              >
                {design.name}
              </h3>
              <p className="text-xs text-text-tertiary">
                {design.extension.toUpperCase()} · {prettyBytes(design.size)} ·{" "}
                {design.createdAt}
              </p>
            </>
          )}
        </div>
        <Button
          aria-label={isMultiple ? "Clear selection" : "Close preview"}
          color="gray"
          isIconOnly
          onPress={onClose}
          variant="unstyled"
        >
          <AccessibleIcon>
            <X className="text-fg-quaternary" />
          </AccessibleIcon>
        </Button>
      </header>
      {isMultiple || isImageDesign(design) ? (
        <div className="flex flex-col gap-md bg-bg-secondary p-lg">
          <p className="text-xs font-medium text-text-tertiary">
            How it looks as{" "}
            {postType.platform === "instagram" ? "an Instagram" : "a Facebook"}{" "}
            {postType.label}
          </p>
          <PublicationPreview
            brandId={brandId}
            designs={designs}
            postType={postType}
          />
        </div>
      ) : (
        <div className="flex h-[420px] items-center justify-center bg-bg-secondary">
          <MediaViewer
            alt={design.name}
            attachmentId={Number(design.id)}
            extension={design.extension}
            showZoomControls={false}
            src={design.url}
            usePreview
          />
        </div>
      )}
    </aside>
  );
}
